import type {
  Application,
  CloudAccount,
  ExtraSourceCloudAccount,
} from '@/domains/application/application.types';
import type {
  Checkpoint,
  CheckpointItem,
} from '@/domains/checkpoint/checkpoint.types';
import type { IObject } from '@/domains/core/core.types';

export type EvaluationStatus =
  | 'PASS'
  | 'FAIL'
  | 'NA'
  | 'EXCEPTION'
  | 'ERROR';

export interface EvaluationAssessmentDataItem extends IObject {
  property?: string;
  value?: IObject[string];
}

export type EvaluationExceptionDetailsValue =
  | string
  | number
  | boolean
  | null;

export interface EvaluationItem {
  checkpointId: string;
  itemId: string;
  itemType: string;
  reference: string;
  remarks: string;
  status: EvaluationStatus;
  system: string;
}

export interface EvaluationMetadata extends Omit<Checkpoint, 'exceptions'> {
  url: string;
  isScored: boolean;
  isMaintenance: boolean;
  isEnabled: boolean;
  displayName: string;
  scorePercent: number;
  totalControls: number;
  group: string | null;
  totalAssessments: number;
  totalPreviewControls: number;
  totalExemptedAssessments: number;
  maintenance?: {
    note?: string;
    eventTime?: string;
  };
  unscoredStatusDetails?: {
    unscoredType: string;
    reason?: string;
    endDate?: string;
    eventTime?: string;
  };
}

export interface EvaluationCheckpoint extends CheckpointItem {
  group?: string;
  remarks: string;
  items: EvaluationItem[];
  assessmentData: EvaluationAssessmentDataItem[];
  metadata: EvaluationMetadata;
  hasException?: boolean;
  exceptionDetails?: EvaluationExceptionDetails | null;
  status: EvaluationStatus;
}

export interface EvaluationExceptionDetails {
  [key: string]: EvaluationExceptionDetailsValue | undefined;
  number?: string | null;
  demand_status?: string | null;
  valid_to?: string | null;
  bit_id?: string | null;
  checkpoint_id?: string | null;
  exception_category?: string | null;
  exception_sub_category?: string | null;
  request_sub_type?: string | null;
  requested_for?: string | null;
  requested_by?: string | null;
}

export interface CheckpointGroup extends CheckpointItem {
  groupName?: string;
  isActive: boolean;
  checkpoints: EvaluationCheckpoint[];
}

export interface CheckpointDivision {
  title: string;
  checkpoints: CheckpointItem[];
  isActive: boolean;
  bugKey: string;
}

export interface Evaluation {
  id: string;
  ordinal?: number;
  score: number;
  bitId: string;
  schemaVersion: string;
  assessmentStatus?: EvaluationStatus | string;
  generatedAt: string;
  inclusiveScore: number;
  totalScoredCheckpoints: number;
  totalCheckpoints: number;
  totalEvaluations: number;
  platform: string;
  isLatest: boolean;
  applicationName: string;
  divisionName: string;
  environmentNames: string[];
  cloudAccounts: CloudAccount[];
  extraSourceCloudAccounts: ExtraSourceCloudAccount[];
  isWKTenant: boolean | null;
  previous: Partial<Evaluation>;
  request: EvaluationRequest;
  metadata: Application;
  args: IObject;
  generatedBy: string | null;
  checkpoints: EvaluationCheckpoint[];
}

export interface EvaluationsResponse {
  items: Evaluation[];
  pageCount: number;
  totalCount: number | null;
  continuationToken: string;
  previousContinuationToken: string;
}

export interface EvaluationRequest {
  args: IObject;
  bitId: string;
  environmentNames: string[];
  headers: {
    'x-orr-automation-definition': string;
    'x-orr-automation-platform': AutomationPlatform;
    'x-orr-caller': string;
    'user-agent': string;
  };
}

export enum AutomationPlatform {
  ADO = 'azuredevops',
  BAMBOO = 'bamboo',
  GITHUB_ACTIONS = 'github',
  JENKINS = 'jenkins',
  NETBOX = 'netbox',
  OCTOPUS_DEPLOY = 'octopus',
  ORR_PORTAL = 'ORR Portal',
  COMFORMANCE = 'conformance',
  ARGO_CD = 'argocd',
  COPADO = 'copado',
  SCHEDULER = 'scheduler',
  SITE_COMPLIANCE_PORTAL = 'Site Compliance Portal',
  UNKNOWN = 'unknown',
}

export interface OngoingEvaluation {
  args?: string[];
  jobId: string;
  statusUrl: string;
  message?: string;
  bitId: string;
  environmentName: string;
  applicationName: string;
  startedAt: string;
  status: 'Running' | 'Failed' | 'Completed' | 'Pending' | 'TimedOut';
  output: EvaluationOutput | null;
  force?: boolean;
  evaluationId?: string | null;
  siteId?: string;
  source?: 'orr' | 'site-compliance';
}

export interface EvaluationStatusResponse {
  jobId: string | null;
  status: 'Running' | 'Failed' | 'Completed';
  output: EvaluationOutput;
}

export interface EvaluationOutput {
  recordId: string;
  status: string;
  runId: string;
  duration: string;
  bitId: string;
  message: string;
}

export interface TriggerEvaluationRequest {
  bitId: string;
  environments: string | string[];
  force?: boolean;
  PassThreshold: number;
  [x: string]: string | string[] | number | boolean | undefined;
}
